import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { HOMEOWNER_DOCUMENT_ACCEPT } from '../utils/fileUpload';
import {
  DEFAULT_FOLDER_NAME,
  SUGGESTED_FOLDER_NAMES,
  cleanFolderName,
  folderNameError,
  sameFolderName,
  documentIsShared,
  folderSubtitle,
} from '../shared/documentFolders';

function resolveFolders(folders, documents) {
  const list = (Array.isArray(folders) ? folders : []).filter(Boolean);
  const known = {};
  list.forEach((folder) => {
    known[folder.id] = true;
  });
  const hasDefault = list.some((folder) => folder.is_default);
  const loose = (Array.isArray(documents) ? documents : []).some((doc) => doc && (!doc.folder_id || !known[doc.folder_id]));
  if (!hasDefault && (list.length === 0 || loose)) {
    list.unshift({
      id: 'unfiled',
      name: DEFAULT_FOLDER_NAME,
      is_default: true,
      shared_with_agent: false,
    });
  }
  return list;
}

function docsInFolder(documents, folder, folders) {
  const known = {};
  folders.forEach((item) => {
    known[item.id] = true;
  });
  return (Array.isArray(documents) ? documents : []).filter((doc) => {
    if (!doc) return false;
    if (doc.folder_id && doc.folder_id === folder.id) return true;
    if (folder.is_default && (!doc.folder_id || !known[doc.folder_id])) return true;
    return false;
  });
}

function ShareSwitch({ checked, label, ariaLabel, detail, onChange, disabled, isDark }) {
  const off = isDark ? '#3a3a3a' : '#e5e7eb';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.98rem' }}>{label}</div>
        {detail ? <div style={{ marginTop: '0.15rem', fontSize: '0.85rem', opacity: 0.8 }}>{detail}</div> : null}
      </div>
      <button
        type="button"
        role="switch"
        className="doc-share-switch"
        aria-checked={checked ? 'true' : 'false'}
        aria-label={ariaLabel || label}
        disabled={disabled}
        onClick={() => {
          if (!disabled) onChange(!checked);
        }}
        style={{
          width: 51,
          height: 31,
          minWidth: 51,
          minHeight: 31,
          padding: 0,
          border: 'none',
          borderRadius: 16,
          background: checked ? '#59c4c4' : off,
          position: 'relative',
          flex: '0 0 auto',
          cursor: disabled ? 'default' : 'pointer',
          boxShadow: 'none',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 22 : 2,
            width: 27,
            height: 27,
            borderRadius: 14,
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
            transition: 'left 0.2s ease',
          }}
        />
      </button>
    </div>
  );
}

/**
 * Documents & Records for the homeowner.
 * Folders open with a tap. Sharing stays off until the homeowner turns it on.
 */
export default function HomeownerDocumentsPanel({
  colors,
  isDark,
  brandColor,
  isMobile,
  readOnly,
  documents,
  folders,
  openFolderId,
  onOpenFolder,
  categories,
  title,
  category,
  file,
  uploading,
  onTitleChange,
  onCategoryChange,
  onFileChange,
  onUpload,
  onView,
  onDownload,
  onDelete,
  onCancelDraft,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onShareFolder,
  onShareDocument,
  onMoveDocument,
  styles,
}) {
  const [open, setOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [folderDraft, setFolderDraft] = useState('');
  const [folderError, setFolderError] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [deleteFolderOpen, setDeleteFolderOpen] = useState(false);
  const fileInputId = 'homeowner-document-file';
  const folderList = resolveFolders(folders, documents);
  const openFolder = folderList.find((folder) => folder.id === openFolderId) || null;
  const openDocs = openFolder ? docsInFolder(documents, openFolder, folderList) : [];

  const closeForm = () => {
    setOpen(false);
    const input = document.getElementById(fileInputId);
    if (input) input.value = '';
  };

  const cancel = () => {
    if (uploading) return;
    closeForm();
    onCancelDraft();
  };

  const submit = async () => {
    if (!category) {
      setUploadError('Choose a category.');
      return;
    }
    setUploadError('');
    const ok = await onUpload();
    if (ok) closeForm();
  };

  const createFolder = async (name) => {
    const cleaned = cleanFolderName(name);
    const message = folderNameError(cleaned);
    if (message) {
      setFolderError(message);
      return;
    }
    if (folderList.some((folder) => sameFolderName(folder.name, cleaned))) {
      setFolderError('A folder with that name already exists.');
      return;
    }
    setFolderError('');
    if (onCreateFolder) await onCreateFolder(cleaned);
    setFolderDraft('');
    setNewFolderOpen(false);
  };

  const card = {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: isDark ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
  };

  const missingSuggestions = SUGGESTED_FOLDER_NAMES.filter((name) => (
    !folderList.some((folder) => sameFolderName(folder.name, name))
  ));

  return (
    <div data-screen={openFolder ? 'folder' : 'folder-list'}>
      <style>{`
        .doc-upload-panel {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.28s ease;
        }
        .doc-upload-panel.is-open {
          grid-template-rows: 1fr;
        }
        .doc-upload-panel-inner {
          overflow: hidden;
          min-height: 0;
        }
        .doc-upload-toggle,
        .doc-folder-row,
        .doc-back-row {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          min-height: 56px;
          margin: 0;
          padding: 0.65rem 0.9rem;
          border: none;
          background: transparent;
          color: inherit;
          text-align: left;
          cursor: pointer;
          box-shadow: none;
          box-sizing: border-box;
          font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }
        .doc-share-switch {
          box-shadow: none !important;
          transform: none !important;
        }
        .doc-upload-chevron {
          display: flex;
          flex: 0 0 auto;
          transition: transform 0.28s ease;
        }
        @media (prefers-reduced-motion: reduce) {
          .doc-upload-panel,
          .doc-upload-chevron,
          .doc-share-switch span {
            transition: none;
          }
        }
      `}</style>
      <h2 data-tour="documents" style={{ ...styles.sectionTitle, color: colors.text, marginBottom: '1rem' }}>
        Documents & Records
      </h2>

      {!readOnly && (
        <div style={card}>
          <button
            type="button"
            className="doc-upload-toggle"
            aria-expanded={open}
            aria-controls="document-upload-form"
            onClick={() => {
              if (uploading) return;
              setOpen((current) => !current);
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                background: brandColor,
                color: '#083838',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2.75v10.5M2.75 8h10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
            <span style={{ flex: 1, minWidth: 0, fontSize: '1.05rem', fontWeight: 600, color: colors.text, letterSpacing: '-0.01em' }}>
              Upload a document
            </span>
            <span
              aria-hidden="true"
              className="doc-upload-chevron"
              style={{ color: colors.textSecondary, transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          <div
            id="document-upload-form"
            className={`doc-upload-panel${open ? ' is-open' : ''}`}
            aria-hidden={open ? undefined : true}
            inert={open ? undefined : true}
          >
            <div className="doc-upload-panel-inner">
              <div style={{ padding: '0.15rem 0.9rem 0.95rem' }}>
                <div style={styles.formGroup}>
                  <label htmlFor="homeowner-document-title" style={{ ...styles.label, color: colors.text }}>Document Title</label>
                  <input
                    id="homeowner-document-title"
                    type="text"
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    style={{ ...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text }}
                    placeholder="e.g., HVAC Service Receipt - March 2025"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label htmlFor="homeowner-document-category" style={{ ...styles.label, color: colors.text }}>Category</label>
                  <select
                    id="homeowner-document-category"
                    value={category}
                    onChange={(e) => {
                      setUploadError('');
                      onCategoryChange(e.target.value);
                    }}
                    style={{ ...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text }}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label htmlFor={fileInputId} style={{ ...styles.label, color: colors.text }}>File (PDF, JPG, PNG, or HEIC, up to 50 MB)</label>
                  <input
                    id={fileInputId}
                    type="file"
                    accept={HOMEOWNER_DOCUMENT_ACCEPT}
                    onChange={(e) => {
                      const next = e.target.files && e.target.files[0];
                      onFileChange(next || null);
                    }}
                    style={{ ...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text, padding: '0.75rem' }}
                  />
                  {file && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: colors.textSecondary }}>
                      Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </div>
                  )}
                </div>
                {uploadError ? (
                  <p role="alert" style={{ color: '#991b1b', margin: '0 0 0.75rem' }}>{uploadError}</p>
                ) : null}
                <button type="button" onClick={submit} disabled={uploading} style={styles.button}>
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
                <button
                  type="button"
                  onClick={cancel}
                  disabled={uploading}
                  style={{
                    width: '100%',
                    minHeight: 44,
                    margin: 0,
                    padding: '0.65rem 1rem',
                    border: 'none',
                    borderRadius: '0.75rem',
                    background: 'transparent',
                    color: colors.textSecondary,
                    fontWeight: 600,
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    cursor: uploading ? 'default' : 'pointer',
                    boxShadow: 'none',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!openFolder && (
        <div style={{ marginTop: readOnly ? 0 : '0.75rem' }}>
          {!readOnly && (
            <div style={{ ...card, marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="doc-upload-toggle"
                aria-expanded={newFolderOpen}
                onClick={() => {
                  setNewFolderOpen((current) => !current);
                  setFolderError('');
                }}
              >
                <span style={{ flex: 1, fontSize: '1.05rem', fontWeight: 600, color: colors.text }}>New folder</span>
                <span aria-hidden="true" style={{ color: colors.textSecondary }}>{newFolderOpen ? '–' : '+'}</span>
              </button>
              {newFolderOpen && (
                <div style={{ padding: '0 0.9rem 0.9rem' }}>
                  <label htmlFor="homeowner-folder-name" style={{ ...styles.label, color: colors.text }}>Folder name</label>
                  <input
                    id="homeowner-folder-name"
                    type="text"
                    value={folderDraft}
                    maxLength={40}
                    onChange={(e) => setFolderDraft(e.target.value)}
                    style={{ ...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text }}
                    placeholder="Insurance"
                  />
                  {folderError ? <div style={{ color: '#b91c1c', marginBottom: '0.6rem' }}>{folderError}</div> : null}
                  <button type="button" style={styles.button} onClick={() => createFolder(folderDraft)}>Create folder</button>
                </div>
              )}
            </div>
          )}

          {missingSuggestions.length > 0 && !readOnly && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginBottom: '0.85rem' }}>
              {missingSuggestions.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => createFolder(name)}
                  style={{
                    minHeight: 36,
                    padding: '0.35rem 0.75rem',
                    borderRadius: 999,
                    border: `1px solid ${colors.border}`,
                    background: colors.surface,
                    color: colors.text,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                  }}
                >
                  Add {name}
                </button>
              ))}
            </div>
          )}

          <div style={styles.taskList}>
            {folderList.map((folder) => {
              const rows = docsInFolder(documents, folder, folderList);
              const sharedCount = rows.filter((doc) => documentIsShared(doc, folder)).length;
              return (
                <button
                  key={folder.id}
                  type="button"
                  className="doc-folder-row"
                  onClick={() => onOpenFolder && onOpenFolder(folder.id)}
                  style={{ ...card, color: colors.text }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                      <span style={{ fontSize: '1.05rem', fontWeight: 600 }}>{folder.name}</span>
                      {folder.shared_with_agent ? (
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: isDark ? '#d7f7f7' : '#0f766e',
                          background: isDark ? 'rgba(89, 196, 196, 0.22)' : 'rgba(89, 196, 196, 0.2)',
                          borderRadius: 999,
                          padding: '0.15rem 0.5rem',
                        }}
                        >
                          Shared
                        </span>
                      ) : null}
                    </span>
                    <span style={{ display: 'block', marginTop: '0.2rem', color: colors.textSecondary, fontSize: '0.9rem', fontWeight: 500 }}>
                      {folderSubtitle(rows.length, sharedCount)}
                    </span>
                  </span>
                  <span aria-hidden="true" style={{ color: colors.textSecondary }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {openFolder && (
        <div>
          <button
            type="button"
            className="doc-back-row"
            onClick={() => {
              setRenaming(false);
              if (onOpenFolder) onOpenFolder(null);
            }}
            style={{ ...card, marginBottom: '0.75rem', color: colors.text }}
          >
            <span aria-hidden="true" style={{ color: brandColor, fontWeight: 700 }}>‹</span>
            <span style={{ fontWeight: 600 }}>Folders</span>
          </button>

          <div style={{ ...card, padding: '0.9rem', marginBottom: '0.75rem', color: colors.text }}>
            <h3 style={{ margin: '0 0 0.35rem', fontSize: '1.25rem', fontWeight: 650 }}>{openFolder.name}</h3>
            {!readOnly && (
              renaming ? (
                <div>
                  <label htmlFor="homeowner-folder-rename" style={{ ...styles.label, color: colors.text }}>Rename folder</label>
                  <input
                    id="homeowner-folder-rename"
                    type="text"
                    value={renameDraft}
                    maxLength={40}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    style={{ ...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text }}
                  />
                  <button
                    type="button"
                    style={styles.button}
                    onClick={async () => {
                      const message = folderNameError(renameDraft);
                      if (message) return;
                      if (onRenameFolder) await onRenameFolder(openFolder, cleanFolderName(renameDraft));
                      setRenaming(false);
                    }}
                  >
                    Save name
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setRenameDraft(openFolder.name);
                      setRenaming(true);
                    }}
                    style={{ ...styles.modernBtn, background: colors.surface, border: `1px solid ${colors.border}`, color: colors.text, padding: '0.45rem 0.8rem' }}
                  >
                    Rename
                  </button>
                  {!openFolder.is_default && (
                    <button
                      type="button"
                      disabled={openDocs.length > 0}
                      onClick={() => setDeleteFolderOpen(true)}
                      style={{ ...styles.modernBtn, background: colors.surface, border: '1px solid #ef4444', color: '#ef4444', padding: '0.45rem 0.8rem' }}
                    >
                      Delete folder
                    </button>
                  )}
                </div>
              )
            )}
            {!openFolder.is_default && openDocs.length > 0 && !readOnly && !renaming ? (
              <p style={{ margin: '0 0 0.75rem', color: colors.textSecondary, fontSize: '0.9rem' }}>Move the documents out first.</p>
            ) : null}
            <ShareSwitch
              checked={!!openFolder.shared_with_agent}
              label="Share with your sponsor"
              ariaLabel={`Share ${openFolder.name} with your sponsor`}
              detail="Shares every document in this folder."
              isDark={isDark}
              disabled={readOnly}
              onChange={(next) => onShareFolder && onShareFolder(openFolder, next)}
            />
          </div>

          {openDocs.length === 0 ? (
            <div style={{ ...styles.emptyState, background: colors.surface, color: colors.textSecondary }}>
              No documents in this folder
            </div>
          ) : (
            <div style={styles.taskList}>
              {openDocs.map((doc) => {
                const shared = documentIsShared(doc, openFolder);
                const viaFolder = shared && doc.shared_with_agent !== true && openFolder.shared_with_agent;
                let shareDetail = 'Not shared';
                if (doc.shared_with_agent === true) shareDetail = 'Shared';
                else if (viaFolder) shareDetail = 'Shared with this folder';
                return (
                  <div key={doc.id} id={`document-${doc.id}`} style={{ ...styles.taskCard, background: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}>
                    <h4 style={{ margin: '0 0 0.45rem', fontSize: '1.05rem', fontWeight: 650, wordBreak: 'break-word' }}>{doc.title}</h4>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
                      {doc.category ? (
                        <span style={{ background: brandColor + '20', color: isDark ? '#d1d5db' : '#0f766e', fontWeight: 600, padding: '0.25rem 0.6rem', borderRadius: '1rem', fontSize: '0.8rem' }}>
                          {doc.category}
                        </span>
                      ) : null}
                      <span style={{ fontSize: '0.9rem', color: colors.textSecondary }}>{doc.file_name}</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: shared ? (isDark ? '#9ee7e7' : '#0f766e') : colors.textSecondary }}>{shareDetail}</span>
                    </div>
                    <div style={{ marginBottom: '0.85rem' }}>
                      <ShareSwitch
                        checked={doc.shared_with_agent === true}
                        label="Share with your sponsor"
                        ariaLabel={`Share ${doc.title} with your sponsor`}
                        detail={viaFolder ? 'This folder is shared too.' : ''}
                        isDark={isDark}
                        disabled={readOnly}
                        onChange={(next) => onShareDocument && onShareDocument(doc, next)}
                      />
                    </div>
                    {folderList.length > 1 && !readOnly && (
                      <label style={{ display: 'block', marginBottom: '0.75rem', color: colors.textSecondary, fontSize: '0.9rem' }}>
                        Move to
                        <select
                          aria-label={`Move ${doc.title}`}
                          value={doc.folder_id || openFolder.id}
                          onChange={(e) => onMoveDocument && onMoveDocument(doc, e.target.value)}
                          style={{ ...styles.input, marginTop: '0.35rem', background: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}
                        >
                          {folderList.map((folder) => (
                            <option key={folder.id} value={folder.id}>{folder.name}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                      <button type="button" onClick={() => onView(doc)} style={{ ...styles.modernBtn, background: colors.surface, border: `2px solid ${brandColor}`, color: brandColor, padding: '0.5rem 1rem' }}>View</button>
                      <button type="button" onClick={() => onDownload(doc)} style={{ ...styles.modernBtn, background: colors.surface, border: '2px solid #10b981', color: '#10b981', padding: '0.5rem 1rem' }}>Download</button>
                      {!readOnly && (
                        <button type="button" onClick={() => onDelete(doc.id)} style={{ ...styles.modernBtn, background: colors.surface, border: '2px solid #ef4444', color: '#ef4444', padding: '0.5rem 1rem' }}>Delete</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <ConfirmDialog
        open={deleteFolderOpen}
        title="Delete this folder?"
        message="This empty folder will be removed."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onCancel={() => setDeleteFolderOpen(false)}
        onConfirm={() => {
          setDeleteFolderOpen(false);
          if (onDeleteFolder && openFolder) onDeleteFolder(openFolder);
        }}
      />
    </div>
  );
}
